import classNames from 'classnames'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import AtomNotice from '@/components/Atoms/AtomNotice/AtomNotice'

import { formatFileSize } from '../../lib/stockImportsFormat'
import tabCn from '../../StockImportsTab/StockImportsTab.module.scss'
import StockImportsVirtualTable from '../../StockImportsVirtualTable/StockImportsVirtualTable'
import type { ConfigPreviewResponse } from '../../types/stockImports.types'
import cn from '../ConfigModal.module.scss'
import { isZeroQuantityRow, previewColumns } from '../excel/previewColumns'
import { previewColumnsWithPrices } from '../excel/previewPriceColumns'
import type { ConfigReviewSection } from '../lib/configReviewSections'
import reviewCn from './ConfigReviewStep.module.scss'
import PricePreviewSummary from './PricePreviewSummary/PricePreviewSummary'

type ConfigReviewStepProps = {
  hidden: boolean
  sections: ConfigReviewSection[]
  preview: ConfigPreviewResponse | null
  previewLoading: boolean
  isOrganisationSelected: boolean
  onPreview: () => void
}

function ConfigReviewStep({
  hidden,
  sections,
  preview,
  previewLoading,
  isOrganisationSelected,
  onPreview,
}: ConfigReviewStepProps) {
  return (
    <div
      className={classNames(cn.step, hidden && cn.stepHidden)}
      aria-hidden={hidden}
    >
      <div className={reviewCn.sections}>
        {sections.map((section) => (
          <section key={section.title} className={reviewCn.section}>
            <h3 className={reviewCn.sectionTitle}>{section.title}</h3>
            <p className={reviewCn.sectionDescription}>{section.description}</p>
            <dl className={reviewCn.items}>
              {section.items.map((item) => (
                <div key={item.label} className={reviewCn.item}>
                  <dt>{item.label}</dt>
                  <dd
                    title={
                      Array.isArray(item.value)
                        ? item.value.join('\n')
                        : item.value
                    }
                  >
                    {Array.isArray(item.value)
                      ? item.value.map((line) => <span key={line}>{line}</span>)
                      : item.value}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>

      <div className={cn.actionsRow}>
        <AtomButton
          variant="secondary"
          loading={previewLoading}
          onClick={onPreview}
        >
          Предпросмотр
        </AtomButton>
        <span className={reviewCn.previewHint}>
          Покажет первые 15 строк файла так, как их прочитает загрузка.
        </span>
      </div>

      {preview && (
        <div className={reviewCn.preview}>
          <AtomNotice
            type={preview.success ? 'success' : 'error'}
            description={`Всего: ${preview.totalParsed} · корректно: ${preview.validCount} · ошибок: ${preview.errorCount} · файл: ${preview.fileName ?? '—'} (${formatFileSize(preview.fileSize)}, ${preview.fileFormat ?? '—'})`}
          >
            {preview.message}
          </AtomNotice>
          {preview.priceSummary && (
            <PricePreviewSummary
              summary={preview.priceSummary}
              calibration={preview.calibration ?? []}
              isOrganisationSelected={isOrganisationSelected}
            />
          )}
          <StockImportsVirtualTable
            data={preview.previewRows}
            columns={
              preview.priceSummary ? previewColumnsWithPrices : previewColumns
            }
            rowKey={(row) => `${row.sheetName}-${row.rowIndex}`}
            emptyText="Нет строк предпросмотра"
            minTableWidth="100%"
            estimateRowHeight={88}
            height={360}
            organisationSelected={isOrganisationSelected}
            rowClassName={(row) =>
              isZeroQuantityRow(row)
                ? tabCn.previewWarningRow
                : row.status === 'ERROR'
                  ? tabCn.previewErrorRow
                  : undefined
            }
          />
          <div className={tabCn.previewLegend}>
            <span>
              <i className={tabCn.previewLegendError} />
              Красная полоса — строку не удалось разобрать или сопоставить с
              каталогом.
            </span>
            <span>
              <i className={tabCn.previewLegendWarning} />
              Оранжевая — строка распознана, но остаток нулевой.
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default ConfigReviewStep
